import type { Metadata } from "next";

const CANONICAL_SITE_URL = "https://www.sdpsi-formations.fr";
const CANONICAL_HOST = "www.sdpsi-formations.fr";
const SITE_NAME = "SDPSI Formations";
const DEFAULT_TITLE = `${SITE_NAME} | Santé, sécurité et prévention au travail`;
const TITLE_TEMPLATE = `%s | ${SITE_NAME}`;
const DEFAULT_DESCRIPTION =
  "Organisme de formation professionnelle en santé, sécurité et prévention des risques. Formations SST, incendie, habilitations et risques spécifiques.";
const DEFAULT_OG_IMAGE_PATH = "/sdpsi.png";
const DEFAULT_KEYWORDS = [
  "formation sécurité",
  "formation SST",
  "prévention des risques",
  "habilitation électrique",
  "formation incendie",
  "SDPSI Formations",
];

const GOOGLE_SITE_VERIFICATION =
  process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION ?? process.env.GOOGLE_SITE_VERIFICATION;
const TWITTER_SITE = process.env.NEXT_PUBLIC_TWITTER_SITE;
const TWITTER_CREATOR = process.env.NEXT_PUBLIC_TWITTER_CREATOR;

const ENV_SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? process.env.NEXT_PUBLIC_BASE_URL;

export type BuildMetadataParams = {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
};

function isProductionDeployment(): boolean {
  if (process.env.VERCEL_ENV) {
    return process.env.VERCEL_ENV === "production";
  }

  return process.env.NODE_ENV === "production";
}

function resolveSiteUrl(): string {
  if (!ENV_SITE_URL) {
    return CANONICAL_SITE_URL;
  }

  try {
    const withProtocol = /^https?:\/\//i.test(ENV_SITE_URL)
      ? ENV_SITE_URL
      : `https://${ENV_SITE_URL}`;
    const parsedUrl = new URL(withProtocol);
    parsedUrl.protocol = "https:";
    parsedUrl.hostname = CANONICAL_HOST;
    parsedUrl.port = "";

    return parsedUrl.origin;
  } catch {
    return CANONICAL_SITE_URL;
  }
}

export const SITE_URL = resolveSiteUrl();

function normalizePath(path = "/"): string {
  if (!path) {
    return "/";
  }

  if (/^https?:\/\//i.test(path)) {
    const parsedUrl = new URL(path);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  }

  return path.startsWith("/") ? path : `/${path}`;
}

export function absoluteUrl(path = "/"): string {
  const normalizedPath = normalizePath(path);
  return new URL(normalizedPath, `${SITE_URL}/`).toString();
}

function resolveImageUrl(image?: string): string {
  if (!image) {
    return absoluteUrl(DEFAULT_OG_IMAGE_PATH);
  }

  if (/^https?:\/\//i.test(image)) {
    return image;
  }

  return absoluteUrl(image);
}

function buildTitle(title?: string): Metadata["title"] {
  if (!title) {
    return {
      default: DEFAULT_TITLE,
      template: TITLE_TEMPLATE,
    };
  }

  return title;
}

function socialTitleFrom(title?: string): string {
  return title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
}

export function buildMetadata({
  title,
  description,
  path,
  image,
  noindex,
}: BuildMetadataParams = {}): Metadata {
  const canonical = absoluteUrl(path ?? "/");
  const metaDescription = description ?? DEFAULT_DESCRIPTION;
  const socialTitle = socialTitleFrom(title);
  const ogImage = resolveImageUrl(image);
  const indexable = noindex ? false : isProductionDeployment();

  return {
    title: buildTitle(title),
    description: metaDescription,
    keywords: DEFAULT_KEYWORDS,
    metadataBase: new URL(SITE_URL),
    alternates: {
      canonical,
    },
    openGraph: {
      type: "website",
      locale: "fr_FR",
      url: canonical,
      siteName: SITE_NAME,
      title: socialTitle,
      description: metaDescription,
      images: [
        {
          url: ogImage,
          width: 1200,
          height: 630,
          alt: socialTitle,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title: socialTitle,
      description: metaDescription,
      images: [ogImage],
      ...(TWITTER_SITE ? { site: TWITTER_SITE } : {}),
      ...(TWITTER_CREATOR ? { creator: TWITTER_CREATOR } : {}),
    },
    robots: {
      index: indexable,
      follow: indexable,
      googleBot: {
        index: indexable,
        follow: indexable,
      },
    },
    icons: {
      icon: [{ url: "/images/home/sdpsiFavicon.png" }],
      shortcut: ["/images/home/sdpsiFavicon.png"],
      apple: [{ url: "/images/home/sdpsiFavicon.png" }],
    },
    manifest: "/manifest.webmanifest",
    verification: GOOGLE_SITE_VERIFICATION
      ? {
          google: GOOGLE_SITE_VERIFICATION,
        }
      : undefined,
  };
}
